import { useTranslation } from "react-i18next";

import { useHasKey } from "./useHasKey";
import { fallbackSrc, sizesFor, srcSet, type Shot as ShotSpec } from "./shots";

export interface ShotProps {
  shot: ShotSpec;
  /**
   * The i18n key whose text describes what is on the frame. A key, not a string: no
   * user-facing text is allowed to originate in a component, alternative text included.
   */
  altKey: string;
  /**
   * Only the hero frame is eager. Everything below the fold waits, so the first screen is
   * not competing with nine other images for bandwidth.
   */
  priority?: boolean;
  className?: string;
}

/**
 * One screenshot, in a frame.
 *
 * <picture> with three formats in descending order of efficiency: the browser takes the
 * first type it understands, so an old one silently lands on the JPEG. Width and height are
 * always emitted — the aspect ratio then exists before a single byte of image arrives, and
 * the page does not jump as the frames load.
 */
/** `chat-desktop` -> `chatDesktop`. */
function camelCase(name: string): string {
  return name.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

export function Shot({ shot, altKey, priority = false, className }: ShotProps) {
  const { t } = useTranslation();
  const has = useHasKey();
  const sizes = sizesFor(shot);

  /*
   * Alternative text, in order of preference:
   *
   *  1. `shots.<name>.alt` — a description written for this specific frame. Those keys are
   *     being added to the dictionaries now; this reads them the moment they land, per
   *     locale, with no change here.
   *  2. `altKey` — the nearest heading the section already states. A stopgap: it describes
   *     the claim rather than the picture, but it is real translated copy rather than
   *     invented text.
   *  3. Empty — decorative. Reached only if both are missing, and it is the right answer
   *     then: an alt that reads "features.calls.title" aloud is worse than no alt at all.
   */
  // Both spellings of the file stem are accepted — `shots.chat-desktop.alt` and
  // `shots.chatDesktop.alt` — because the dictionary side is being written in parallel and
  // this should not hinge on which of the two the translator picked.
  const ownAltKeys = [`shots.${shot.name}.alt`, `shots.${camelCase(shot.name)}.alt`];
  const ownAltKey = ownAltKeys.find(has);
  const alt = ownAltKey ? t(ownAltKey) : has(altKey) ? t(altKey) : "";

  return (
    <picture className={["shot", `shot--${shot.kind}`, className].filter(Boolean).join(" ")}>
      <source type="image/avif" srcSet={srcSet(shot, "avif")} sizes={sizes} />
      <source type="image/webp" srcSet={srcSet(shot, "webp")} sizes={sizes} />
      <img
        className="shot__img"
        src={fallbackSrc(shot)}
        width={shot.width}
        height={shot.height}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        decoding={priority ? "sync" : "async"}
        // A hero frame is the largest paint on the page; the rest are not worth the hint.
        fetchPriority={priority ? "high" : undefined}
      />
    </picture>
  );
}
